import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useCallback, useContext, useEffect, useMemo, useRef} from "react";
import arePropsEqual from "react-fast-compare";
import * as SlateReact from "slate-react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {Spring} from "@khanacademy/wonder-blocks-layout";
import {semanticColor, spacing} from "@khanacademy/wonder-blocks-tokens";
import Tooltip from "@khanacademy/wonder-blocks-tooltip";
import {
    useGuideAudioController,
    useMobileIconMenu,
    useMobileIconMenuAPI,
} from "@khan/guide-core";
import {isActivitiesPage} from "@khan/guide-core-helpers";
import {
    renderElement,
    renderLeaf,
    renderPlaceholder,
} from "@khan/guide-math-editor";
import {useMathEditorContext} from "@khan/guide-math-editor-context";
import {useGuideState} from "@khan/guide-state";
import type {ChatInputMetadata, ImageInput} from "@khan/guide-types";
import {
    CHAT_INPUT_EXPANDED_PADDING,
    CHAT_INPUT_ICON_HEIGHT,
    isSubmitOnlyAssignment,
} from "@khan/guide-utils";
import {RequestInfo} from "@khan/shared-core";
import {AppShellContext} from "@khan/shared-core/context";
import {
    ReferenceList,
    resetEditor,
    scrollSelectionIntoViewOverride,
} from "@khan/slate-helpers";
import {useLocation} from "@khan/urls";

import {removeAttachedImagesFromGuideState} from "../image-attach/remove-attached-images-from-guide-state.ts";
import {updateImageGuideStateForAttach} from "../image-attach/update-image-guide-state-for-attach.ts";
import {useSupportImageInput} from "../image-attach/use-support-image-input.ts";
import {ConnectedMicrophoneButton} from "../microphone-button.tsx";
import PreviewModeSubmitModal from "../preview-mode-submit-modal.tsx";
import {PreviewModeContext} from "../PreviewModeContext.ts";
import {useKhanmigoSidebarState} from "../sidebar/context/use-khanmigo-sidebar-state.ts";
import {SubmitAssignmentModalLauncher} from "../submit-assignment-modal-launcher.tsx";

import {AddReferenceButton} from "./add-reference-button.tsx";
import {AssignmentsNavigatePrompt} from "./assignments-navigate-prompt.tsx";
import {ChatBoxImageAttachment} from "./chat-box-image-attachment.tsx";
import {ConnectedHeadphoneButton} from "./connected-headphone-button.tsx";
import {ImageInputModalLauncher} from "./image-input-modal-launcher.tsx";
import {MathInputButton} from "./math-input-button.tsx";
import {useAssignments} from "./use-assignments.ts";
import {useHandleSend} from "./use-handle-set.ts";
import {useKeyHandlers} from "./use-key-handlers.ts";
import {useReferences} from "./use-references.ts";
import {useSpeechToText} from "./use-speech-to-text.ts";

import paperAirplaneIcon from "@phosphor-icons/core/fill/paper-plane-tilt-fill.svg";
import plusCircleIcon from "@phosphor-icons/core/regular/plus-circle.svg";

export type ChatInputComponent = React.ComponentType<Props>;

/**
 * ChatInputConfig is a configuration object that allows the caller to enable or
 * disable specific tools in the chat UI. If this configuration is not provided,
 * all tools will be enabled.
 * @property inlineSendButton - If true, the send button will render inline with
 * the input on desktop.
 * @property mathKeypad - If true, the math keypad will be enabled in the chat
 * UI. If false, the math keypad will not appear.
 * @property teacherReference - If true, the teacher reference tool (@) will be
 * enabled in the chat UI, though it will not render unless the user is a
 * teacher. If false, the teacher reference tool will not appear.
 */
export interface ChatInputConfig {
    inlineSendButton?: boolean;
    mathKeypad?: boolean;
    teacherReference?: boolean;
}

export interface Props {
    disableSend?: boolean;
    onSend: (metadata: ChatInputMetadata) => unknown;
    message: string;
    onImageDelete?: () => void;
    expanded?: boolean;
    config?: ChatInputConfig;
    replaceSendButton?: JSX.Element;
    placeholder?: string;
    disableInput?: boolean;
}

// While in companion mode, we only want to refocus the input if
// the chat window is open.
// eslint-disable-next-line react-refresh/only-export-components -- porting from old code.
export const useShouldAutoFocusInput = () => {
    const {isCompanionMode} = useKhanmigoSidebarState();
    const open = useGuideState("open");

    return isCompanionMode ? open : true;
};

const useResetEditorOnThreadChange = (autoFocus: boolean) => {
    const editor = SlateReact.useSlate();
    const threadId = useGuideState("threadId");

    useEffect(() => {
        // reset the editor when the threadId changes
        resetEditor(editor, autoFocus);
        // We only want to invoke this effect when the thread ID changes
        // eslint-disable-next-line react-hooks/exhaustive-deps -- porting from old code.
    }, [editor, threadId]);
};

function getPlaceholderText(pendingImage: ImageInput | undefined) {
    return pendingImage ? t`Ask about this image...` : t`Ask away...`;
}

const ChatInput = ({
    disableSend,
    onSend,
    message,
    expanded,
    config = {
        mathKeypad: true,
        teacherReference: true,
    },
    replaceSendButton,
    placeholder,
    onImageDelete,
    disableInput,
}: Props): React.ReactElement => {
    const {pathname} = useLocation();
    const submitOnlyActivity = isSubmitOnlyAssignment(pathname);
    const chatInputs: Props = {
        disableSend,
        disableInput,
        onSend,
        message,
        expanded,
        config,
        replaceSendButton,
        placeholder,
        onImageDelete,
    };
    if (submitOnlyActivity) {
        return SubmitButtonOnlyChatInput(chatInputs);
    }

    return ChatBoxChatInput(chatInputs);
};

const SubmitButtonOnlyChatInput = (chatInputs: Props): React.ReactElement => {
    const {isAnyActive: isAnyMathActive} = useMathEditorContext();
    const {pathname} = useLocation();

    const isMobile = RequestInfo.isMobile;
    const disabled = !!chatInputs.disableSend;
    const injectTextDisabled = disabled || isAnyMathActive;

    const {promptWhenNavigateAway, assignmentButtonProps} = useAssignments(
        chatInputs.message,
    );
    const {showReferencesList} = useReferences({
        enabled: chatInputs.config?.teacherReference,
        injectTextDisabled,
    });
    return (
        <>
            {promptWhenNavigateAway && <AssignmentsNavigatePrompt />}
            {showReferencesList && <ReferenceList />}
            <View
                style={[
                    styles.inputWrapper,
                    chatInputs.expanded && styles.expanded,
                    chatInputs.expanded &&
                        isActivitiesPage(pathname) &&
                        styles.expandedInActivities,
                ]}
            >
                <View
                    style={[
                        styles.inputButtons,
                        isMobile && styles.mobileIconMenu,
                    ]}
                >
                    <Spring />
                    <View style={styles.rightInputButtons}>
                        <View style={styles.blockSendButtons}>
                            {assignmentButtonProps && (
                                <SubmitAssignmentModalLauncher
                                    {...assignmentButtonProps}
                                    style={styles.blockSendButton}
                                />
                            )}
                        </View>
                    </View>
                </View>
            </View>
        </>
    );
};

const ChatBoxChatInput = (chatInputs: Props): React.ReactElement => {
    const {pathname} = useLocation();
    const {isAnyActive: isAnyMathActive} = useMathEditorContext();

    const isMobile = RequestInfo.isMobile;
    const isMobileIconMenuOpen = useMobileIconMenu();
    const setIsMobileIconMenuOpen = useMobileIconMenuAPI();
    const {entrypoint} = useContext(AppShellContext);
    const iconSize = entrypoint === "classroom" ? "medium" : "small";

    const showBottomRow = !isMobile || isMobileIconMenuOpen;
    const disabled = !!chatInputs.disableSend;
    const disableInput = !!chatInputs.disableInput;

    // If the feature is disabled, or the context does not support image input,
    // we hide the image input launcher entirely. Also if input is disabled, we
    // hide the image input launcher.
    const imageInputAllowed = useSupportImageInput() && !disableInput;

    const pendingImage = useGuideState("images").find(
        (imageData) => !imageData.interactionID,
    );

    // "injectText" refers to any time we want to add text to a text input.
    // Since we are dealing with an array of inputs, we need to target the
    // correct input, determine cursor position, and update the array.
    // Since we don't want speech-to-text for math inputs, we disable it here.
    const injectTextDisabled = disabled || disableInput || isAnyMathActive;

    const {speechInProgress, speech, showMicButton} = useSpeechToText();
    const messageContainsSpeechToTextContentRef = useRef(false);
    const onEnableSpeechToText = useCallback(() => {
        messageContainsSpeechToTextContentRef.current = true;
    }, []);

    const autoFocus = useShouldAutoFocusInput();
    useResetEditorOnThreadChange(autoFocus);

    // disable send if message is empty or only whitespace
    const isSendDisabled = disabled || !chatInputs.message.replace(/\s/g, "");
    const {isVoiceOn, selectedVoice} = useGuideAudioController();

    const {
        references,
        isReferencesActive,
        showReferencesList,
        showReferencesButton,
    } = useReferences({
        enabled: chatInputs.config?.teacherReference,
        injectTextDisabled,
    });

    const onSend = chatInputs.onSend;
    const handleSend = useHandleSend({
        isSendDisabled,
        onSend,
        messageContainsSpeechToTextContentRef,
        isVoiceOn,
        selectedVoice,
    });

    const handleAttachImages = (images: Array<ImageInput>) => {
        // First, clear any pending attached images
        removeAttachedImagesFromGuideState();
        // Then add the newly attached images
        updateImageGuideStateForAttach(images);
    };

    const handleUnattachImages = () => {
        removeAttachedImagesFromGuideState();
    };

    const extraKeyDownArgs = useMemo(
        () => ({
            references,
        }),
        [references],
    );

    const {handleKeyDown, handlePaste} = useKeyHandlers({
        handleSend,
        enableReferenceKeyHandlers: isReferencesActive,
        extraKeyDownArgs,
    });

    const {
        currentThreadSubmitted,
        showSubmitAssignmentButton,
        promptWhenNavigateAway,
        assignmentButtonProps,
    } = useAssignments(chatInputs.message);

    const previewModeContextValue = useContext(PreviewModeContext);
    const submitButton = previewModeContextValue !== undefined && (
        <PreviewModeSubmitModal currentMessage={chatInputs.message} />
    );
    // Currently, replaceSendButton serves Writing Coach, which does not
    // support image uploads. If that changes, we may need to revise this
    // logic to handle attached image messaging.
    const sendButtonDisabled = isSendDisabled || currentThreadSubmitted;
    const connectedHeadphoneButtonDisabled = disabled || disableInput;
    const mathInputButtonDisabled = speechInProgress || disableInput;
    const sendIconButton = chatInputs.replaceSendButton ? (
        chatInputs.replaceSendButton
    ) : pendingImage && sendButtonDisabled ? (
        <Tooltip content={t`Ask a question to send`}>
            <IconButton
                onClick={handleSend}
                disabled={isSendDisabled || currentThreadSubmitted}
                icon={paperAirplaneIcon}
                kind="tertiary"
                size={iconSize}
                style={styles.sendIconButton}
                aria-label={t`Send message`}
            />
        </Tooltip>
    ) : (
        <IconButton
            onClick={handleSend}
            disabled={sendButtonDisabled}
            icon={paperAirplaneIcon}
            kind="tertiary"
            size={iconSize}
            style={styles.sendIconButton}
            aria-label={t`Send message`}
        />
    );
    const showSendIconButton =
        !isMobile || (isMobile && !showSubmitAssignmentButton);

    const inputPlaceholder =
        chatInputs.placeholder ?? getPlaceholderText(pendingImage);

    const renderInlineSendButton =
        (chatInputs.config?.inlineSendButton ?? false) || isMobile;

    return (
        <>
            {promptWhenNavigateAway && <AssignmentsNavigatePrompt />}
            {showReferencesList && <ReferenceList />}
            <View
                style={[
                    styles.inputWrapper,
                    chatInputs.expanded && styles.expanded,
                    chatInputs.expanded &&
                        isActivitiesPage(pathname) &&
                        styles.expandedInActivities,
                ]}
            >
                <View style={styles.inputButtons}>
                    <View style={styles.autosizeWrapper}>
                        <View style={styles.inputFieldWrapper}>
                            {pendingImage && (
                                <ChatBoxImageAttachment
                                    image={pendingImage.imageURL}
                                    onDelete={handleUnattachImages}
                                />
                            )}
                            <View style={styles.scrollableWrapper}>
                                <SlateReact.Editable
                                    renderLeaf={renderLeaf}
                                    renderElement={renderElement}
                                    renderPlaceholder={renderPlaceholder}
                                    placeholder={inputPlaceholder}
                                    autoFocus={autoFocus}
                                    onKeyDown={handleKeyDown}
                                    scrollSelectionIntoView={
                                        scrollSelectionIntoViewOverride
                                    }
                                    // prevents browser focus ring which
                                    // blocks cursor from being visible
                                    style={{outline: "none"}}
                                    data-testid="chat-input-textarea"
                                    onPaste={handlePaste}
                                    aria-label={t`Type message (do NOT share any personal data)`}
                                    readOnly={disableInput}
                                />
                            </View>
                        </View>
                        {isMobile && (
                            <IconButton
                                onClick={() => {
                                    setIsMobileIconMenuOpen(
                                        !isMobileIconMenuOpen,
                                    );
                                }}
                                disabled={disabled}
                                kind="tertiary"
                                testId="plus-icon-button"
                                style={styles.plusIconButton}
                                icon={plusCircleIcon}
                                size="small"
                                aria-label={t`Toggle chat options`}
                            />
                        )}
                    </View>
                    <ConnectedHeadphoneButton
                        disabled={connectedHeadphoneButtonDisabled}
                    />
                    {renderInlineSendButton ? (
                        // Show the send button inline with the input
                        <View style={styles.inlineSendButtons}>
                            {assignmentButtonProps && (
                                <SubmitAssignmentModalLauncher
                                    {...assignmentButtonProps}
                                    style={{width: "100%"}}
                                />
                            )}
                            {previewModeContextValue !== undefined &&
                                submitButton}
                            {showSendIconButton && sendIconButton}
                        </View>
                    ) : showMicButton ? (
                        <ConnectedMicrophoneButton
                            speech={speech}
                            disabled={injectTextDisabled}
                            onEnable={onEnableSpeechToText}
                        />
                    ) : null}
                </View>
                {showBottomRow && (
                    // We show the bottom row of input buttons on desktop
                    // version, or when the mobile icon menu is open.
                    <View
                        style={[
                            styles.inputButtons,
                            isMobile && styles.mobileIconMenu,
                        ]}
                    >
                        <View style={styles.leftInputButtons}>
                            {imageInputAllowed && (
                                <ImageInputModalLauncher
                                    onAttachImages={handleAttachImages}
                                />
                            )}
                            <MathInputButton
                                disabled={mathInputButtonDisabled}
                                mathButtonEnabled={
                                    chatInputs.config?.mathKeypad
                                }
                            />
                            {showReferencesButton && <AddReferenceButton />}
                        </View>
                        <Spring />
                        <View style={styles.rightInputButtons}>
                            {!renderInlineSendButton && (
                                // Show the send button below the input
                                <View style={styles.blockSendButtons}>
                                    {assignmentButtonProps && (
                                        <SubmitAssignmentModalLauncher
                                            {...assignmentButtonProps}
                                            style={styles.blockSendButton}
                                        />
                                    )}
                                    {previewModeContextValue !== undefined &&
                                        submitButton}
                                    {showSendIconButton && sendIconButton}
                                </View>
                            )}
                            {isMobile && (
                                <Button
                                    size="small"
                                    kind="tertiary"
                                    onClick={() => {
                                        setIsMobileIconMenuOpen(false);
                                    }}
                                    testId="close-icon-menu-button"
                                >
                                    {t`Close`}
                                </Button>
                            )}
                        </View>
                    </View>
                )}
            </View>
        </>
    );
};

const MemoizedChatInput = React.memo(ChatInput, arePropsEqual);

export {MemoizedChatInput as ChatInput};

const styles = StyleSheet.create({
    inputFieldWrapper: {
        width: "100%",
        justifyContent: "center",
    },
    blockSendButtons: {
        flexDirection: "row",
    },
    inlineSendButtons: {
        minInlineSize: "fit-content",
    },
    blockSendButton: {
        marginInlineEnd: spacing.xSmall_8,
    },
    autosizeWrapper: {
        flexDirection: "row",
        maxHeight: "10em",
        width: "100%",
        fontSize: 16,
        justifyContent: "center",
        backgroundColor: semanticColor.core.background.base.subtle,
        borderRadius: 4,
        overflow: "hidden",
        color: semanticColor.core.foreground.neutral.strong,
        ":focus-within": {
            outline: `1px solid ${semanticColor.focus.outer}`,
        },
    },
    // Making autosizeWrapper scrollable caused the Slate Editable
    // to get cropped. Making Slate Editable scrollable screwed up padding.
    // This element is just to balance the two problems.
    scrollableWrapper: {
        overflowY: "auto",
        padding: `${spacing.xxSmall_6}px ${spacing.xSmall_8}px`,
    },
    leftInputButtons: {
        flexDirection: "row",
        justifyContent: "flex-start",
        gap: spacing.xSmall_8,
    },
    inputButtons: {
        flexDirection: "row",
        justifyContent: "center",
        gap: spacing.xSmall_8,
        padding: `${spacing.xSmall_8}px ${spacing.medium_16}px`,
    },
    rightInputButtons: {
        flexDirection: "row",
        justifyContent: "flex-end",
        alignItems: "flex-start",
    },
    inputWrapper: {
        borderBlockStart: `1px solid ${semanticColor.core.border.neutral.subtle}`,
        borderRadius: "0 0 8px 8px",
        backgroundColor: semanticColor.core.background.base.default,
        display: "flex",
        flexDirection: "column",
    },
    expanded: {
        // As designed: Add padding to align with the left hand side
        // of the chat bubble.
        padding: `${spacing.xSmall_8}px ${CHAT_INPUT_EXPANDED_PADDING}px`,
    },
    expandedInActivities: {
        // As designed: Add padding to align with the left hand side
        // of the chat bubble.
        padding: `${spacing.xSmall_8}px ${CHAT_INPUT_EXPANDED_PADDING - 18}px`,
    },
    sendIconButton: {
        alignSelf: "center",
    },
    plusIconButton: {
        alignSelf: "center",
        marginInlineEnd: spacing.xSmall_8,
        width: CHAT_INPUT_ICON_HEIGHT,
        height: CHAT_INPUT_ICON_HEIGHT,
    },
    mobileIconMenu: {
        // As designed.
        height: 44,
    },
});
